import Papa from "papaparse";
import React, { useContext, useState } from "react";
import { DataHandlerContext } from "../../providers/DataHandlerProvider";
import ExportCSVButton from "../buttons/ExportCSVButton";

function AddFileModal({
  text,
  eventSeriesId,
  event_title,
  eventId,
  setModal,
  setListOfUserSubmissions,
  listOfUserSubmissions,
}) {
  const { handleAddSubmission } = useContext(DataHandlerContext);

  const [data, setData] = useState({});
  const [error, setError] = useState("");
  const [file, setFile] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [submissionData, setSubmissionData] = useState({});
  const allowedExtensions = ["csv"];

  const handleFileChange = (e) => {
    e.preventDefault();
    setError("");

    if (e.target.files.length) {
      const inputFile = e.target.files[0];

      const fileExtension = inputFile?.type.split("/")[1];
      if (!allowedExtensions.includes(fileExtension)) {
        setError("Please input a CSV file");
        return;
      }

      setFile(inputFile);
    }
  };

  const handleParse = async (e) => {
    e.preventDefault();
    if (!file) {
      setError("Please select a CSV file first");
      return;
    }
    
    setIsLoading(true);
    const reader = new FileReader();

    reader.onload = async ({ target }) => {
      try {
        const csv = Papa.parse(target.result, {
          header: true,
          delimiter: ",",
          skipEmptyLines: true,
        });

        const data = csv.data;
        setData(data);
        
        const response = await handleAddSubmission(eventSeriesId, eventId, data);
        console.log("Response:", response);

        setListOfUserSubmissions((prevState) => {
          const currentEventSubmissions = prevState[eventId] || [];
          console.log("CurrentEventSubmissions:", currentEventSubmissions);

          return {
            ...prevState,
            [eventId]: [...currentEventSubmissions, ...data],
          };
        });
        
        setModal(false);
      } catch (error) {
        console.error("Error processing file:", error);
        setError("Error processing file. Please check format and try again.");
        setIsLoading(false);
      }
    };
    
    reader.onerror = () => {
      setError("Error reading file");
      setIsLoading(false);
    };
    
    reader.readAsText(file);
  };

  return (
    <div className="fixed top-0 left-0 h-screen w-screen bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-6 border border-gray-300 rounded-lg shadow-xl max-w-4xl w-full mx-4">
        
        
        <div className="flex justify-between items-center border-b pb-4 mb-6">
          <h2 className="text-2xl font-semibold text-gray-800">{text}</h2>
          <button 
            className="bg-gray-200 hover:bg-gray-300 text-gray-800 font-bold w-8 h-8 rounded-full flex items-center justify-center transition-colors"
            aria-label="Close modal"
            onClick={() => setModal(false)}>
            ✕
          </button>
        </div>
        
       
        {error && (
          <div className="mb-4 p-3 bg-red-100 text-red-700 rounded-md">
            <p className="flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          </div>
        )}
        
        <div className="grid md:grid-cols-5 gap-6">
         
          <div className="md:col-span-3 space-y-6">
            <div className="bg-blue-50 p-4 rounded-md border border-blue-200">
              <h3 className="text-lg font-medium text-blue-800 mb-2">CSV Upload Instructions</h3>
              <ul className="list-disc pl-5 space-y-1 text-sm text-blue-800">
                <li>File must be in CSV format</li>
                <li>CSV must have headers for each column</li>
                <li>Required fields: first_name, last_name, email</li>
                <li>Optional fields: job_title, organisation, message</li>
                <li>All attendees in the file will be added to this event</li>
              </ul>
            </div>
            
            <form className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Select CSV file to upload
                </label>
                <input 
                  className="block w-full text-sm text-gray-900 border border-gray-300 rounded-md cursor-pointer bg-gray-50 focus:outline-none p-2"
                  onChange={handleFileChange} 
                  name="file" 
                  type="file"
                  accept=".csv" 
                />
                <p className="mt-1 text-sm text-gray-500">
                  Selected: {file ? file.name : "No file chosen"}
                </p>
              </div>
              
              <button
                type="submit"
                disabled={isLoading || !file}
                className={`w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 ${isLoading || !file ? 'opacity-50 cursor-not-allowed' : 'hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'}`}
                onClick={handleParse}
              >
                {isLoading ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  "Upload Attendees"
                )}
              </button>
            </form>
          </div>
          
         
          <div className="md:col-span-2 flex flex-col space-y-6">
            <div className="bg-gray-50 p-4 rounded-md border border-gray-200">
              <h3 className="text-lg font-medium text-gray-800 mb-2">Export Existing Data</h3>
              <p className="text-sm text-gray-600 mb-4">
                Download the current list of attendees for this event. This will include all existing registrations.
              </p>
              
              <div className="mt-2">
                <ExportCSVButton
                  data={listOfUserSubmissions}
                  fileName={`${event_title} - ${eventId} - ${new Date().toLocaleDateString()}`}
                />
              </div>
            </div>
            
            <div className="bg-green-50 p-4 rounded-md border border-green-200">
              <h3 className="text-lg font-medium text-green-800 mb-2">What happens next?</h3>
              <p className="text-sm text-green-700">
                After upload, attendees will be added to this event. They will <strong>not</strong> receive automatic confirmation emails through this upload process.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AddFileModal;